import { useParams } from 'react-router-dom';
import { useSuspenseQuery } from '@tanstack/react-query';
import { fetchPledge } from 'api/pledges';
import { addInstallmentStatus } from 'assets/js/pages/pledges/utils';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { isoDateFromServer } from 'helpers/dates';
import { conditionallyFetchOrNull } from 'helpers/reactQuery';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Pledge } from 'types/domain';

const useCurrentPledge = () => {
    const { pledge_id } = useParams<{
        pledge_id: string;
    }>();
    const pledgeId = parseInt(pledge_id);
    const { accountId } = useCurrentAccount();

    return useSuspenseQuery({
        queryKey: accountQueryKeys.pledgeById(accountId, pledgeId),
        queryFn: () => {
            return fetchPledge(accountId, pledgeId);
        },
        select: mapInstallmentStatus,
    });
};

const useCurrentPossiblePledge = () => {
    const { pledge_id } = useParams<{
        pledge_id: string;
    }>();
    const { accountId } = useCurrentAccount();
    const hasPledgeId = !!pledge_id;
    const pledgeId = hasPledgeId ? parseInt(pledge_id) : -1;

    return useSuspenseQuery({
        queryKey: accountQueryKeys.pledgeById(accountId, pledgeId),
        queryFn: () =>
            conditionallyFetchOrNull(() => fetchPledge(accountId, pledgeId), hasPledgeId),
        select: pledge => {
            if (pledge) {
                return mapInstallmentStatus(pledge);
            }

            return null;
        },
        retry: false,
    });
};

const mapInstallmentStatus = (data: Pledge) => {
    return {
        ...data,
        installments: addInstallmentStatus(
            data.installments.map(item => ({
                ...item,
                date: isoDateFromServer(item.date).endOf('day'),
            })),
            data.amount_fulfilled,
        ),
    };
};

export { useCurrentPledge, useCurrentPossiblePledge };
